import { useCallback, useEffect, useState } from "react";
import {
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
} from "services/api";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";

const defaultNotifChannel = {
  data: [],
  pagination: null,
  error: null,
  loading: false,
};

const defaultFilterOptions = {
  search: "",
  currentPage: 1,
  totalPages: 1,
};

const useNotifChannels = () => {
  const [notifChannels, setNotifChannels] = useState(defaultNotifChannel);

  const [filterOptions, setFilterOptions] = useState(defaultFilterOptions);

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    limit: LIMIT_DATA_PER_PAGE,
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      console.log("search", e.target.value);
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const fetchNotifChannels = useCallback(async () => {
    setNotifChannels((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/setting/whatsapp${queryString}`);
      if (response.success) {
        setNotifChannels({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          // totalPages: response.pagination.last_page
        }));
      }
    } catch (error) {
      setNotifChannels({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchNotifChannels();
  }, [queryString]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = notifChannels.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5, // Optional: number of visible pages in pagination (default is 5)
  });

  return {
    notifChannels,
    setNotifChannels,
    handleSearch,
    filterOptions,
    Pagination,
    refetch: fetchNotifChannels,
  };
};

// export default useNotifChannels;
export { useNotifChannels };

export const useCreateNotifChannel = ({ notifChannels, setNotifChannels }) => {
  const createNotifChannel = async (data) => {
    setNotifChannels((prevState) => ({
      ...prevState,
      loading: true,
    }));
    try {
      const response = await postRequest("/setting/whatsapp", data);
      if (response.success) {
        setNotifChannels((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return {
          success: true,
          message: "Notification channel has been created",
        };
      }
    } catch (error) {
      setNotifChannels((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return {
        success: false,
        message: "Failed to create notification channel",
      };
    }
  };

  return { createNotifChannel };
};

export const useUpdateNotifChannel = ({ notifChannels, setNotifChannels }) => {
  const updateNotifChannel = async (id, data) => {
    setNotifChannels((prevState) => ({
      ...prevState,
      loading: true,
    }));
    try {
      const response = await patchRequest(`/setting/whatsapp/${id}`, data);
      if (response.success) {
        setNotifChannels((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return {
          success: true,
          message: "Notification channel has been updated",
        };
      }
    } catch (error) {
      setNotifChannels((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return {
        success: false,
        message: "Failed to update notification channel",
      };
    }
  };

  return { updateNotifChannel };
};

export const useRemoveNotifChannel = ({ notifChannels, setNotifChannels }) => {
  const removeNotifChannel = async (id) => {
    setNotifChannels((prevState) => ({
      ...prevState,
      loading: true,
    }));
    try {
      const response = await deleteRequest(`/setting/whatsapp/${id}`);
      if (response.success) {
        setNotifChannels((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return {
          success: true,
          message: "Notification channel has been deleted",
        };
      }
    } catch (error) {
      setNotifChannels((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return {
        success: false,
        message: "Failed to delete notification channel",
      };
    }
  };

  return { removeNotifChannel };
};

// useMailTargets Hook
const useMailTargets = () => {
  const [mailTargets, setMailTargets] = useState(defaultNotifChannel);
  const [filterOptions, setFilterOptions] = useState(defaultFilterOptions);

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    limit: 10, // Tentukan limit data per halaman sesuai kebutuhan
  });

  // Search handler
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  // Fetch Mail Targets
  const fetchMailTargets = useCallback(async () => {
    setMailTargets((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/setting/mailtarget${queryString}`);
      if (response.success) {
        setMailTargets({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          // totalPages: response.pagination.last_page
        }));
      }
    } catch (error) {
      setMailTargets({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  // Fetch data on filter change
  useEffect(() => {
    fetchMailTargets();
  }, [queryString]);

  // Pagination handler
  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = mailTargets.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5, // Optional: number of visible pages in pagination
  });

  return {
    mailTargets,
    setMailTargets,
    handleSearch,
    filterOptions,
    Pagination,
    refetch: fetchMailTargets,
  };
};

export { useMailTargets };

export const useCreateMailTarget = ({ mailTargets, setMailTargets }) => {
  const createMailTarget = async (data) => {
    setMailTargets((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await postRequest("/setting/mailtarget", data);
      if (response.success) {
        setMailTargets((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return { success: true, message: "Mail target has been created" };
      }
    } catch (error) {
      setMailTargets((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return { success: false, message: "Failed to create mail target" };
    }
  };

  return { createMailTarget };
};

//useSMTP hook /setting/smtp
// Hook untuk mengelola data SMTP
// In useNotifChannels.js

const defaultSmtpSettings = {
  data: [],
  pagination: null,
  error: null,
  loading: false,
};

// const useSMTP = () => {
//     const [smtpSettings, setSmtpSettings] = useState(defaultNotifChannel);

//     const fetchSMTP = useCallback(async () => {
//       setSmtpSettings((prevState) => ({
//         ...prevState,
//         loading: true,
//       }));

//       try {
//         const response = await getRequest("/setting/smtp");
//         if (response.success) {
//           setSmtpSettings({
//             data: response.data,
//             error: null,
//             loading: false,
//           });
//           console.log("SMTP", response.data);
//         }
//       } catch (error) {
//         setSmtpSettings({
//           data: [],
//           error: error.message,
//           loading: false,
//         });
//         console.error("Failed to fetch SMTP settings", error.message);
//       }
//     }, []);

//     useEffect(() => {
//       fetchSMTP();
//     }, [fetchSMTP]);

//     return { smtpSettings, setSmtpSettings, fetchSMTP };
//   };
// export { useSMTP };

const useSMTPs = () => {
  const [smtpSettings, setSmtpSettings] = useState(defaultSmtpSettings);

  const fetchSMTP = useCallback(async () => {
    setSmtpSettings((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest("/setting/smtp");
      if (response.success) {
        setSmtpSettings({
          data: response.data,
          error: null,
          loading: false,
        });
        console.log("SMTP", response.data);
      }
    } catch (error) {
      setSmtpSettings({
        data: [],
        error: error.message,
        loading: false,
      });
      console.error("Failed to fetch SMTP settings", error.message);
    }
  }, []);

  useEffect(() => {
    fetchSMTP();
  }, [fetchSMTP]);

  return { smtpSettings, setSmtpSettings, refetch: fetchSMTP };
};

export { useSMTPs };

// Create SMTP settings
export const useCreateSMTP = ({ smtpSettings, setSmtpSettings }) => {
  const createSMTP = async (data) => {
    setSmtpSettings((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await postRequest("/setting/smtp", data);
      if (response.success) {
        setSmtpSettings((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return { success: true, message: "SMTP settings have been created" };
      }
    } catch (error) {
      setSmtpSettings((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return { success: false, message: "Failed to create SMTP settings" };
    }
  };

  return { createSMTP };
};
